import { useAuth } from '../context/AuthContext';
import { CreditCard, Check, Zap } from 'lucide-react';

const PLANS = [
  {
    key:      'free',
    name:     'Free',
    price:    '$0',
    period:   '/mo',
    features: ['10 scans / day', '1 API key', 'Basic risk scoring', '7-day history'],
    cta:      'Current Plan',
    color:    'slate',
  },
  {
    key:      'starter',
    name:     'Starter',
    price:    '$29',
    period:   '/mo',
    features: ['100 scans / day', '3 API keys', 'Advanced risk scoring', '30-day history', 'Export reports'],
    cta:      'Upgrade',
    color:    'cyan',
    popular:  true,
  },
  {
    key:      'pro',
    name:     'Pro',
    price:    '$99',
    period:   '/mo',
    features: ['Unlimited scans', '10 API keys', 'Full analysis suite', 'Unlimited history', 'Webhook alerts', 'Priority support'],
    cta:      'Upgrade',
    color:    'purple',
  },
  {
    key:      'enterprise',
    name:     'Enterprise',
    price:    'Custom',
    period:   '',
    features: ['Unlimited everything', 'Custom integrations', 'SLA guarantee', 'Dedicated support', 'On-premise option'],
    cta:      'Contact Us',
    color:    'slate',
  },
];

const BTN_CLASS = {
  cyan:   'bg-[#00E5FF] hover:bg-[#00cce5] text-black',
  purple: 'bg-[#7C3AED] hover:bg-[#6d35d4] text-white',
  slate:  'bg-white/[0.07] hover:bg-white/[0.12] text-slate-300',
};

const BORDER_CLASS = {
  cyan:   'border-[#00E5FF]/25 shadow-[0_0_28px_rgba(0,229,255,0.06)]',
  purple: 'border-[#7C3AED]/25',
  slate:  'border-white/[0.06]',
};

export default function Billing() {
  const { user } = useAuth();
  const current = user?.plan || 'free';

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-white">Billing & Plans</h1>
        <p className="text-slate-500 text-sm mt-0.5">Choose the plan that fits your security needs</p>
      </div>

      {/* Current plan banner */}
      <div className="glass-card p-4 mb-8 border border-[#00E5FF]/15 flex items-center gap-3">
        <CreditCard size={16} className="text-[#00E5FF]" />
        <span className="text-slate-400 text-sm">
          Current plan:{' '}
          <span className="text-white font-bold capitalize">{current}</span>
        </span>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PLANS.map(plan => {
          const isCurrent = plan.key === current;
          return (
            <div
              key={plan.key}
              className={`glass-card p-5 flex flex-col relative border ${BORDER_CLASS[plan.color]}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00E5FF] text-black text-[10px] font-bold px-2.5 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap shadow-lg">
                  <Zap size={9} />
                  Popular
                </div>
              )}

              <div className="mb-5">
                <div className="text-slate-500 text-xs font-medium mb-2">{plan.name}</div>
                <div className="flex items-end gap-1">
                  <span className="text-2xl font-bold text-white">{plan.price}</span>
                  {plan.period && (
                    <span className="text-slate-600 text-xs pb-0.5">{plan.period}</span>
                  )}
                </div>
              </div>

              <ul className="space-y-2 flex-1 mb-5">
                {plan.features.map(f => (
                  <li key={f} className="flex items-start gap-2 text-xs text-slate-500">
                    <Check size={11} className="text-[#00E5FF] mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                disabled={isCurrent}
                className={[
                  'w-full py-2 rounded-xl text-xs font-bold transition-all',
                  isCurrent
                    ? 'bg-white/[0.05] text-slate-600 cursor-default'
                    : BTN_CLASS[plan.color],
                ].join(' ')}
              >
                {isCurrent ? 'Current Plan' : plan.cta}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
